import { DeleteOutlined, DisconnectOutlined, GoogleOutlined, MobileOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Form, Input, Modal, Typography } from 'antd';

const { Title, Text } = Typography;
import { useMutation } from '@tanstack/react-query';
import axios from '../../config/axios';
import { handleAPIError } from '../../utils/helpers';
import AppLayout from '../../layouts/app.layout';


interface ActionCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    confirmTitle: string;
    confirmContent: string;
    okText: string;
    action: string;
    danger?: boolean;
}

const ActionCard = ({ icon, title, description, confirmTitle, confirmContent, okText, action, danger = false }: ActionCardProps) => {
    const [form] = Form.useForm<{ imei: string }>();

    const run = useMutation({
        mutationFn: async (imei: string) => {
            await axios.post('/admin/super/devices/manage-action', { action, imei }, { withCredentials: true });
        },
        onSuccess: () => {
            form.resetFields();
        },
        onError: handleAPIError,
    });

    const handleSubmit = ({ imei }: { imei: string }) => {
        Modal.confirm({
            title: confirmTitle,
            content: (
                <div>
                    <p>{confirmContent}</p>
                    <p className="mt-2 font-mono text-sm text-gray-500">IMEI: {imei}</p>
                </div>
            ),
            okText,
            okType: danger ? 'danger' : 'primary',
            cancelText: 'Cancel',
            onOk: () => run.mutate(imei),
        });
    };

    return (
        <Card className="shadow-sm">
            <div className="mb-3 flex items-center gap-3">
                <span className="text-2xl text-gray-500">{icon}</span>
                <div>
                    <Title level={5} style={{ margin: 0 }}>{title}</Title>
                    <Text type="secondary" className="text-sm">{description}</Text>
                </div>
            </div>
            <Form form={form} layout="inline" onFinish={handleSubmit}>
                <Form.Item
                    name="imei"
                    rules={[
                        { required: true, message: 'Enter IMEI' },
                        { len: 15, message: 'IMEI must be 15 digits' },
                        { pattern: /^\d+$/, message: 'IMEI must be numeric' },
                    ]}
                    className="flex-1"
                >
                    <Input placeholder="Enter device IMEI (15 digits)" maxLength={15} style={{ width: 280 }} />
                </Form.Item>
                <Form.Item>
                    <Button
                        type="primary"
                        danger={danger}
                        htmlType="submit"
                        loading={run.isPending}
                    >
                        Run
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

const ManageDevices = () => {
    return (
        <AppLayout title="Manage Devices" subtitle="Send remote actions directly to a device by IMEI">
            <div className="space-y-4">
                <Alert
                    type="warning"
                    showIcon
                    message="Super Admin only. All actions are logged and cannot be undone."
                />

                <ActionCard
                    icon={<DeleteOutlined />}
                    title="Reset Device"
                    description="Factory reset the device — wipes all data permanently."
                    confirmTitle="Factory reset this device?"
                    confirmContent="This will permanently wipe all data from the device. The customer will lose everything on the device."
                    okText="Reset Device"
                    action="reset"
                    danger
                />

                <ActionCard
                    icon={<DisconnectOutlined />}
                    title="Release Device"
                    description="Releases the device from MDM management (sends UNINSTALL command)."
                    confirmTitle="Release this device from MDM?"
                    confirmContent="This will remove the device from MDM management. The Zazu app will be uninstalled and the device will no longer be monitored."
                    okText="Release Device"
                    action="release_device"
                    danger
                />

                <ActionCard
                    icon={<GoogleOutlined />}
                    title="Unsuspend Google Play"
                    description="Unsuspends the Google Play Store app (com.android.vending) on the device."
                    confirmTitle="Unsuspend Google Play?"
                    confirmContent="This will unsuspend the Google Play Store on the device."
                    okText="Unsuspend"
                    action="unsuspend_google_play"
                />

                <ActionCard
                    icon={<MobileOutlined />}
                    title="Clear Cache & Storage — Zazu Mobile"
                    description="Clears cache and storage for the Zazu Mobile app (com.zazu.mobile) on the device."
                    confirmTitle="Clear Zazu Mobile cache and storage?"
                    confirmContent="This will clear all cache and storage data for the Zazu Mobile app. The customer will need to log in again."
                    okText="Clear Cache"
                    action="clear_zazu_cache"
                />
            </div>
        </AppLayout>
    );
};

export default ManageDevices;
